<template>
  <div class="col-sm-8">
    <vue-multiselect
      v-model="selectedOption"
      :options="options"
      track-by="id"
      label="value"
      :multiple="false"
      :searchable="true"
      :allow-empty="true"
      :close-on-select="true"
      :required="required"
      @select="onOptionSelected" />
  </div>

  <div class="col-sm-1">
    <hs-button
      class="add-button"
      :rounded="true"
      variant="secondaryColorHollow"
      :icon-left="IconAdd"
      :borderless="true"
      @click="showAddCustomFieldValue = true" />
  </div>

  <add-organization-custom-field-value
    v-if="showAddCustomFieldValue"
    :organization-custom-field="organizationCustomField"
    :custom-field-values="options"
    @add="onOrganizationCustomFieldAdd"
    @cancel="showAddCustomFieldValue = false" />
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import VueMultiselect from 'vue-multiselect/src/Multiselect.vue';

  import HsButton from '@/common/components/ui/Button/Button.vue';
  import { IconAdd } from '@/common/icons/monochrome';
  import { OrganizationCustomField } from '@/common/models';
  import OrganizationCustomFieldValue from '@/common/models/organization-custom-fields/OrganizationCustomFieldValue';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';

  import AddOrganizationCustomFieldValue from './AddOrganizationCustomFieldValue.vue';

  interface IProps {
    organizationCustomField: OrganizationCustomField;
    options: OrganizationCustomFieldValue[];
    required: boolean;
    preSelectedOption?: OrganizationCustomFieldValue | null;
    classNames?: string;
  }

  const { required, options, organizationCustomField, preSelectedOption } = defineProps<IProps>();

  const emit = defineEmits(['select']);

  const { OrganizationCustomFields } = mapOrgAppState();
  const { getOrganizationCustomFields, postOrganizationCustomFieldsValue } = mapOrgAppActions();

  const selectedOption = ref<OrganizationCustomFieldValue>(null);
  const showAddCustomFieldValue = ref(false);
  const allOrgCustomFields = computed(() => OrganizationCustomFields?.organizationCustomFields);
  const organizationCustomFieldId = computed(() => organizationCustomField?.id ?? -1);

  function onOptionSelected(selectedOption: OrganizationCustomFieldValue) {
    emit('select', selectedOption);
  }

  function assignPreSelectedOption() {
    selectedOption.value = preSelectedOption;
  }

  async function onOrganizationCustomFieldAdd(value: OrganizationCustomFieldValue) {
    await postOrganizationCustomFieldsValue({
      instances: value,
    });
    await getOrganizationCustomFields();

    const selectedOrgCustomField = allOrgCustomFields.value.find(
      (field) => field.id === organizationCustomFieldId.value,
    );
    const selectedCustomValue = selectedOrgCustomField?.organizationCustomValues?.find((v) => v.value === value.value);

    emit('select', selectedCustomValue);
    showAddCustomFieldValue.value = false;
  }

  watch(
    () => preSelectedOption,
    (newValue) => {
      if (newValue !== selectedOption.value) {
        assignPreSelectedOption();
      }
    },
  );

  onMounted(() => {
    assignPreSelectedOption();
  });

  defineExpose({ showAddCustomFieldValue });
</script>

<style lang="scss">
  .col-sm-1 {
    .btn.add-button {
      padding: 0.8rem;
    }
  }
</style>
